import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toaster from 'react-hot-toast';

// APIs
import { fetchProcessingServiceOrdersPerStore } from 'api/admin/devices/devices';

// components
import TeamMemberSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/TeamMemberSelection';
import TransactionAmountSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/TransactionAmountSelection';
import ServiceOrderSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/fullService/ServiceOrderSelection';
import DeviceTransactionFinalReviewScreen from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/DeviceTransactionFinalReviewScreen';

// Utils
import { convertDollarsToCents } from 'utils/convertMoney';

const FullServiceDeviceTransactionSimulator = (props) => {
  const { device, onBack, onSubmit, teamMembers } = props;
  const fullServiceDeviceTransactionScreens = {
    USER_SELECTION: 'USER_SELECTION',
    SERVICE_ORDER_SELECTION: 'SERVICE_ORDER_SELECTION',
    TRANSACTION_AMOUNT: 'TRANSACTION_AMOUNT',
    FINAL_REVIEW: 'FINAL_REVIEW',
  };

  // form data
  const [wizardStep, setWizardStep] = useState(
    fullServiceDeviceTransactionScreens.USER_SELECTION,
  );
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedTeamMemberDisplay, setSelectedTeamMemberDisplay] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedServiceOrderId, setSelectedServiceOrderId] = useState(null);
  const [selectedOrderCode, setSelectedOrderCode] = useState(null);
  const [selectedTransactionAmountInDollars, setSelectedTransactionAmountInDollars] =
    useState(null);

  /**
   * fetch the list of turn serviceType values on initialization
   */
  const { data: serviceOrders } = useQuery(
    ['fetchProcessingServiceOrdersPerStore'],
    async () => {
      const { data } = await fetchProcessingServiceOrdersPerStore(device.storeId);
      return data.serviceOrders;
    },
    {
      onError: (error) => {
        toaster(`Error fetching service orders: ${error?.response?.data?.error}`);
      },
    },
  );

  /**
   * send formatted data from the wizard steps to the parent to complete
   */
  const submitFullServiceSimulationData = () => {
    const dataToSubmit = {
      deviceId: device.id,
      machineId: device.machineId,
      transactionAmount: convertDollarsToCents(selectedTransactionAmountInDollars),
      centsCustomerId: selectedCustomerId,
      storeId: device.storeId,
      userId: selectedUserId,
      serviceOrderId: selectedServiceOrderId,
    };

    return onSubmit(dataToSubmit);
  }

  /**
   * navigate backwards in the wizard based on current progress in the wizard
   */
  const handleOnBackFunctionality = () => {
    switch (wizardStep) {
      case fullServiceDeviceTransactionScreens.USER_SELECTION:
        return onBack();
      case fullServiceDeviceTransactionScreens.SERVICE_ORDER_SELECTION:
        setSelectedUserId(null);
        setSelectedTeamMemberDisplay(null);
        return setWizardStep(fullServiceDeviceTransactionScreens.USER_SELECTION);
      case fullServiceDeviceTransactionScreens.TRANSACTION_AMOUNT:
        setSelectedCustomerId(null);
        setSelectedServiceOrderId(null);
        setSelectedOrderCode(null);
        return setWizardStep(fullServiceDeviceTransactionScreens.SERVICE_ORDER_SELECTION);
      case fullServiceDeviceTransactionScreens.FINAL_REVIEW:
        setSelectedTransactionAmountInDollars(null);
        return setWizardStep(fullServiceDeviceTransactionScreens.TRANSACTION_AMOUNT);
      default:
        return onBack();
    }
  };

  /**
   * determine the logic for disabling the final submit button
   */
  const disableFinalSubmitButton = () => {
    return !selectedUserId || !selectedServiceOrderId || !selectedTransactionAmountInDollars;
  };

  /**
   * render the final review screen prior to submitting the simulation event
   */
  const renderFinalReviewScreen = () => {
    return (
      <DeviceTransactionFinalReviewScreen
        deviceId={device.id}
        machineId={device.machineId}
        selectedTransactionAmount={selectedTransactionAmountInDollars}
        selectedTeamMemberDisplay={selectedTeamMemberDisplay}
        selectedOrderCode={selectedOrderCode}
        onBack={handleOnBackFunctionality}
        onNext={submitFullServiceSimulationData}
        disableFinalSubmitButton={disableFinalSubmitButton}
      />
    )
  }

  /**
   * navigate forwards to the final review screen once transaction amount is identified
   */
  const handleOnNextForTransactionAmountSelection = () => {
    setWizardStep(fullServiceDeviceTransactionScreens.FINAL_REVIEW);
  };

  /**
   * render the transaction amount input
   */
  const renderTransactionAmountSelection = () => {
    return (
      <TransactionAmountSelection
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForTransactionAmountSelection}
        selectedTransactionAmountInDollars={selectedTransactionAmountInDollars}
        setSelectedTransactionAmount={(amount) => {
          setSelectedTransactionAmountInDollars(Number(amount));
        }}
      />
    );
  };

  /**
   * Navigate forward in the wizard to the transaction amount screen
   */
  const handleOnNextForServiceOrderSelection = () => {
    setWizardStep(fullServiceDeviceTransactionScreens.TRANSACTION_AMOUNT);
  }

  /**
   * set the centsCustomerId, serviceOrderId, and orderCode selections for the service order assigned to the turn
   * 
   * @param {object} serviceOrderData
   */
  const setValuesForServiceOrderSelection = (serviceOrderData) => {
    setSelectedOrderCode(serviceOrderData.orderCode);
    setSelectedCustomerId(serviceOrderData.centsCustomerId);
    setSelectedServiceOrderId(serviceOrderData.id);
  }

  /**
   * Render the screen that allows you to choose the service order
   */
  const renderServiceOrderSelection = () => {
    return (
      <ServiceOrderSelection
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForServiceOrderSelection}
        serviceOrders={serviceOrders}
        selectedOrderCode={selectedOrderCode}
        onValueChange={setValuesForServiceOrderSelection}
        selectedServiceOrderId={selectedServiceOrderId}
      />
    );
  }

  /**
   * Determine which series of steps to show based on the team member selection
   */
  const handleOnNextForUserSelection = () => {
    setWizardStep(fullServiceDeviceTransactionScreens.SERVICE_ORDER_SELECTION);
  };

  /**
   * set the user ID and display name selections for the team member assigned to the turn
   * 
   * @param {object} userData 
   */
  const setValuesForUserSelection = (userData) => {
    setSelectedUserId(userData.userId);
    setSelectedTeamMemberDisplay(`${userData.firstName} ${userData.lastName}`);
  }

  /**
   * render the team member selection screen
   */
  const renderTeamMemberSelection = () => {
    return (
      <TeamMemberSelection
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForUserSelection}
        selectedTeamMemberDisplay={selectedTeamMemberDisplay}
        selectedUserId={selectedUserId}
        onValueChange={setValuesForUserSelection}
        teamMembers={teamMembers}
      />
    );
  };

  /**
   * Determine the proper form/wizard step based on state of the component
   */
  const renderFullServiceTransactionWizardSteps = () => {
    switch (wizardStep) {
      case fullServiceDeviceTransactionScreens.USER_SELECTION:
        return renderTeamMemberSelection();
      case fullServiceDeviceTransactionScreens.SERVICE_ORDER_SELECTION:
        return renderServiceOrderSelection();
      case fullServiceDeviceTransactionScreens.TRANSACTION_AMOUNT:
        return renderTransactionAmountSelection();
        case fullServiceDeviceTransactionScreens.FINAL_REVIEW:
          return renderFinalReviewScreen();
      default:
        return renderTeamMemberSelection();
    }
  };

  return <>{renderFullServiceTransactionWizardSteps()}</>;
};

export default FullServiceDeviceTransactionSimulator;
